/**
 * Warms up lazy images before the browser would fetch them.
 *
 * Every image in this theme is server-rendered with a real `src`/`srcset` and
 * `loading="lazy"`. There is no LQIP, no `data-src`, no JS loader. That is deliberate and
 * worth keeping: the markup works without JavaScript, srcset negotiation stays with the
 * browser, and crawlers see the real image.
 *
 * The cost of that choice is that the pre-load distance is NOT author-controllable. The
 * browser picks it, varies it by effective connection type, and has changed the numbers
 * across versions. On a slow link the distance shrinks in wall-clock terms, so an image can
 * still be arriving when the customer has already scrolled to it.
 *
 * This closes exactly that gap and nothing else: an observer with a generous margin flips
 * `loading` to `eager` shortly before the image would come into view, which resumes the
 * deferred fetch immediately. Nothing else changes. `src` and `srcset` are never touched,
 * so with JavaScript off the page behaves exactly as before, which is the whole reason not
 * to reach for a data-src loader here.
 *
 * Deliberately NOT done:
 *   - No blanket `loading="eager"` in Liquid. That would put every below-fold image into
 *     the initial request burst and compete with the LCP image.
 *   - No `fetchpriority`. On a lazy image it only reorders a request that has already been
 *     triggered; it does not trigger one sooner.
 *   - No warm-up under Save-Data. A customer who asked for less traffic gets less.
 */
(function () {
  'use strict';

  /* Roughly one and a half viewports on a phone. Big enough to cover a fast scroll on a
     slow link, small enough that an abandoned page does not fetch the whole catalogue. */
  var MARGIN = '1200px 0px';

  if (!('IntersectionObserver' in window)) return;

  /* An explicit request for less traffic outweighs a nicer scroll. */
  var conn = navigator.connection;
  if (conn && conn.saveData) return;

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var img = entry.target;
        observer.unobserve(img);
        /* Already done by the browser in the meantime: leave it alone. */
        if (img.complete && img.naturalWidth > 0) return;
        img.loading = 'eager';
      });
    },
    { rootMargin: MARGIN, threshold: 0 }
  );

  function warm(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var images = root.querySelectorAll('img[loading="lazy"]');
    for (var i = 0; i < images.length; i += 1) {
      var img = images[i];
      if (img.complete && img.naturalWidth > 0) continue;
      observer.observe(img);
    }
  }

  warm(document);

  /*
    Impact re-renders whole sections through the Section Rendering API (cart drawer, facets)
    and two lists append pages as you scroll, so images arrive after first paint. Without
    this they would keep the browser's own distance and the fix would only ever apply to the
    first render.
  */
  if ('MutationObserver' in window) {
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i += 1) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j += 1) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.tagName === 'IMG') {
            if (node.getAttribute('loading') === 'lazy') observer.observe(node);
          } else {
            warm(node);
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
